import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { envoyerPhotoProfil, retirerPhotoProfil } from '../services/api';

/** Formats et taille acceptés côté serveur par PhotoProfilService. */
const FORMATS = ['image/png', 'image/jpeg', 'image/webp'];
const TAILLE_MAX = 5 * 1024 * 1024;

interface Props {
  photoUrl: string | null;
  /** Sert d'initiale quand aucune photo n'est déposée. */
  nom: string;
  onNotice: (notice: { type: 'success' | 'error'; message: string }) => void;
}

/**
 * Photo de profil du membre : dépôt, remplacement et retrait.
 *
 * L'image était auparavant saisie sous forme d'URL, ce qui obligeait à
 * l'héberger ailleurs — inutilisable depuis un téléphone. Elle est désormais
 * choisie dans la galerie ou prise à l'appareil, puis recadrée par le serveur.
 */
export default function PhotoProfil({ photoUrl, nom, onNotice }: Props) {
  const queryClient = useQueryClient();
  const champFichier = useRef<HTMLInputElement>(null);
  const [apercu, setApercu] = useState<string | null>(null);

  const envoi = useMutation({
    mutationFn: (fichier: File) => envoyerPhotoProfil(fichier),
    onSuccess: (d) => {
      onNotice({ type: 'success', message: d.message });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => onNotice({ type: 'error', message: e.message }),
    // L'aperçu local ne survit pas à la réponse : au-delà, c'est l'image
    // recadrée par le serveur qui fait foi.
    onSettled: () => setApercu(null),
  });

  const retrait = useMutation({
    mutationFn: retirerPhotoProfil,
    onSuccess: (d) => {
      onNotice({ type: 'success', message: d.message });
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => onNotice({ type: 'error', message: e.message }),
  });

  const enCours = envoi.isPending || retrait.isPending;

  function choisir(fichier: File | undefined) {
    if (!fichier) return;

    // Contrôles côté navigateur : ils évitent un aller-retour réseau pour un
    // refus que le serveur prononcera de toute façon.
    if (!FORMATS.includes(fichier.type)) {
      onNotice({ type: 'error', message: 'Formats acceptés : PNG, JPG, WEBP.' });
      return;
    }
    if (fichier.size > TAILLE_MAX) {
      onNotice({ type: 'error', message: "L'image ne doit pas dépasser 5 Mo." });
      return;
    }

    setApercu(URL.createObjectURL(fichier));
    envoi.mutate(fichier);
  }

  const affichee = apercu ?? photoUrl;

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => champFichier.current?.click()}
        disabled={enCours}
        title="Changer ma photo de profil"
        className="relative group w-20 h-20 rounded-full overflow-hidden bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-bold text-2xl shrink-0 disabled:opacity-60"
      >
        {affichee ? (
          <img src={affichee} alt={nom} className="w-full h-full object-cover" />
        ) : (
          nom.charAt(0).toUpperCase()
        )}

        <span className="absolute inset-0 bg-black/45 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
          {envoi.isPending ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Camera className="w-6 h-6" />
          )}
        </span>
      </button>

      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => champFichier.current?.click()}
            disabled={enCours}
            className="btn-outline inline-flex items-center gap-2 text-sm py-1.5 px-3 disabled:opacity-50"
          >
            <Camera className="w-4 h-4" />
            {photoUrl ? 'Changer ma photo' : 'Ajouter une photo'}
          </button>

          {photoUrl && (
            <button
              type="button"
              onClick={() => retrait.mutate()}
              disabled={enCours}
              title="Retirer ma photo de profil"
              className="inline-flex items-center gap-2 text-sm py-1.5 px-3 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {retrait.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Retirer
            </button>
          )}
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          PNG, JPG ou WEBP, 5 Mo au plus. L'image est recadrée en carré.
        </p>
      </div>

      <input
        ref={champFichier}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          choisir(e.target.files?.[0]);
          // Remis à zéro pour que redéposer le même fichier déclenche l'envoi.
          e.target.value = '';
        }}
      />
    </div>
  );
}
